import { useState, useEffect, useCallback, useRef } from "react";

import { fetchOurApi } from "../utils/apiCalls";
import { useCustomerPaymentMethod } from "../hooks/stripe";

import { useAccountData } from "../firebase";
import { StripeCustomerContext } from "./stripeCustomerContext";

export { StripeCustomerContext };

// Sentinel distinguishing "haven't decided anything for this provider
// instance yet" from "already handled a real (possibly falsy) stripeCustomerId" --
// stripeCustomerId itself can legitimately be falsy (no Stripe customer), so a
// plain null/undefined ref can't tell those two cases apart.
const NOT_YET_FETCHED = Symbol("stripe-customer-not-yet-fetched");

export const StripeCustomerContextProvider = ({ children }) => {
  const [{ stripeCustomerId }, accountDataLoading] = useAccountData();

  const {
    paymentMethod,
    isPaymentMethodLoading,
    refetchCustomerPaymentMethod,
  } = useCustomerPaymentMethod();

  const [isSubscriptionsLoading, setIsSubscriptionsLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);

  // Tracks whichever customerId is the most recently requested, so a response
  // for a superseded customer (account switched away while it was in flight)
  // can't overwrite the current account's already-applied result.
  const activeCustomerIdRef = useRef(null);

  const refetchCustomerSubscriptions = useCallback(
    async (givenStripeCustomerId) => {
      const customerId = givenStripeCustomerId || stripeCustomerId;

      activeCustomerIdRef.current = customerId;

      if (!customerId) {
        return setIsSubscriptionsLoading(false);
      }

      return await fetchOurApi({
        path: "/stripe/get-customer-subscriptions",
        method: "POST",
        data: {
          customerId,
        },
        callback: (subscriptions) => {
          if (customerId !== activeCustomerIdRef.current) {
            return;
          }

          if (!!subscriptions && subscriptions.length) {
            setSubscriptions(subscriptions);
          }

          return setIsSubscriptionsLoading(false);
        },
      });
    },
    [stripeCustomerId]
  );

  // isSubscriptionsLoading is otherwise one-shot: once it latches false (e.g.
  // stripeCustomerId was falsy on an earlier account's render), switching to a
  // *different* account would never retry or clear the previous account's
  // subscriptions -- true whether the new account's stripeCustomerId is truthy
  // (never retried) or falsy (previous account's subscriptions kept showing).
  // Track the id already accounted for and reopen the loading gate whenever it
  // changes, in either direction.
  const fetchedStripeCustomerIdRef = useRef(NOT_YET_FETCHED);

  useEffect(() => {
    if (stripeCustomerId === fetchedStripeCustomerIdRef.current) {
      return;
    }

    const isFirstFetch =
      fetchedStripeCustomerIdRef.current === NOT_YET_FETCHED;
    fetchedStripeCustomerIdRef.current = stripeCustomerId;

    if (isFirstFetch) {
      return;
    }

    setSubscriptions([]);
    setIsSubscriptionsLoading(true);
  }, [stripeCustomerId]);

  useEffect(() => {
    if (
      !accountDataLoading &&
      isSubscriptionsLoading &&
      !subscriptions.length
    ) {
      refetchCustomerSubscriptions(stripeCustomerId);
    }
  }, [
    accountDataLoading,
    isSubscriptionsLoading,
    stripeCustomerId,
    subscriptions,
    refetchCustomerSubscriptions,
  ]);

  return (
    <StripeCustomerContext.Provider
      value={{
        subscriptions,
        isSubscriptionsLoading,
        refetchCustomerSubscriptions,
        paymentMethod,
        isPaymentMethodLoading,
        refetchCustomerPaymentMethod,
        isNotStripeCustomer: !stripeCustomerId,
      }}
    >
      {children}
    </StripeCustomerContext.Provider>
  );
};
